import { createSelector } from 'reselect';
import { EDUCATOR_ROLE, STUDENT_ROLE } from '@core/constants/space';
import {
  accessingSpaceAsStaff,
  isInstructorInSpace,
} from '@core/selectors/classroom';
import { getAllSpaces } from '@core/selectors/space/space.selectors';
import { currentUser, currentUserId } from '@core/selectors/user';
import { AulaState, Space } from '@core/types/state';
import { HomeViewRole } from '@app/constants/home';
import getStarredSpaces from '@core/spaceSelector/utils/getStarredSpaces';
import { mapHomeSpaceToCard } from '@app/utils/mapHomeSpaceToCard';
import { SpaceWidgetCardData } from '@app/components/presentational/Home/widgets/spaceWidget/SpaceWidget.types';

export const getHomeState = (state: AulaState) => state.home;

const getUserRoleInSpace = (
  state: AulaState,
  spaceId: string,
  userId: string | null
): HomeViewRole => {
  if (!userId) {
    return STUDENT_ROLE;
  }

  if (
    accessingSpaceAsStaff(state)(spaceId) ||
    isInstructorInSpace(state)(userId, spaceId)
  ) {
    return EDUCATOR_ROLE;
  }

  return STUDENT_ROLE;
};

export const getDefaultHomeViewRole = (state: AulaState): HomeViewRole => {
  const userId = currentUserId(state);
  const spaces = Object.values(getAllSpaces(state) || {});

  const hasEducatorSpace = spaces.some(
    (space) =>
      getUserRoleInSpace(state, space.objectId, userId) === EDUCATOR_ROLE
  );

  return hasEducatorSpace ? EDUCATOR_ROLE : STUDENT_ROLE;
};

export const getHomeViewRole = (state: AulaState): HomeViewRole =>
  getHomeState(state)?.viewRole ?? getDefaultHomeViewRole(state);

export const getCanSwitchHomeViewRole = createSelector(
  [(state: AulaState) => state, getAllSpaces, currentUserId],
  (state, spaces, userId) => {
    if (!userId) {
      return false;
    }

    let hasEducatorSpace = false;
    let hasStudentSpace = false;

    Object.values(spaces || {}).forEach((space) => {
      if (getUserRoleInSpace(state, space.objectId, userId) === EDUCATOR_ROLE) {
        hasEducatorSpace = true;
      } else {
        hasStudentSpace = true;
      }
    });

    return hasEducatorSpace && hasStudentSpace;
  }
);

export const getHomeSpacesForView = createSelector(
  [(state: AulaState) => state, getHomeViewRole],
  (state, viewRole): Space[] => {
    const userId = currentUserId(state);
    return Object.values(getAllSpaces(state) || {})
      .filter((space) => !space.archived)
      .filter(
        (space) =>
          getUserRoleInSpace(state, space.objectId, userId) === viewRole
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }
);

export const getHomeUserFirstName = (state: AulaState): string =>
  currentUser(state)?.firstName || '';

export const getHomeUserPreferences = createSelector(
  [(state: AulaState) => state.home],
  (home) => home?.widgetPreferences || []
);

export const getHomeSpacesState = (state: AulaState) => getHomeState(state);

export const getHomeSpacesLoading = (state: AulaState): boolean =>
  getHomeState(state)?.spacesLoading ?? false;

export const getHomeSpacesError = (state: AulaState): boolean =>
  getHomeState(state)?.spacesError ?? false;

export const getHomeSpacesErrorType = (
  state: AulaState
): 'network' | 'api' | null => getHomeState(state)?.spacesErrorType ?? null;

export const getHomeSpacesHasMore = (state: AulaState): boolean =>
  getHomeState(state)?.spacesHasMore ?? true;

type RecentlyViewedSpaceItem = {
  spaceId?: string;
  lastNavigation?: string;
};

export const getRecentlyViewedNavigationBySpaceId = (
  state: AulaState
): Record<string, string> => {
  const spaceList = (state.spaceSelector?.recentlyViewedSpaces?.spaceList ??
    []) as RecentlyViewedSpaceItem[];

  return spaceList.reduce<Record<string, string>>((acc, item) => {
    if (item?.spaceId && item?.lastNavigation) {
      acc[item.spaceId] = item.lastNavigation;
    }
    return acc;
  }, {});
};

export const getHomeSpaceCardsForView = createSelector(
  [getHomeState, getHomeViewRole, getRecentlyViewedNavigationBySpaceId],
  (home, viewRole, lastNavBySpaceId): SpaceWidgetCardData[] => {
    if (!home) {
      return [];
    }

    const apiCards = home.spaces
      .filter((space) => space.role === viewRole)
      .map((space) => {
        const card = mapHomeSpaceToCard(
          space,
          home.spacesProgressById[space.spaceId],
          home.spacesEngagementById[space.spaceId]
        );

        return {
          ...card,
          lastNavigation: lastNavBySpaceId[space.spaceId] ?? null,
        };
      });

    return apiCards;
  }
);

export const getHomeStarredSpaces = createSelector(
  [(state: AulaState) => state, getHomeViewRole, getAllSpaces, currentUserId],
  (state, viewRole, spacesMap, userId): Space[] => {
    const spaces = Object.values(spacesMap || {});

    // Filter starred spaces by archived status and the active viewRole (Educator/Student)
    const filteredSpaces = spaces
      .filter((space) => !space.archived)
      .filter(
        (space) =>
          getUserRoleInSpace(state, space.objectId, userId) === viewRole
      );

    return getStarredSpaces(filteredSpaces);
  }
);
